import {AssetFilterEnum, AssetTypeFilterEnum, AssetTypeLockStatusEnum, BaseFilterEnum, TechnologyFilterEnum} from "../enums";
import {LockStatusFilterEnum} from "../enums/filters/lock-status-filter.enum";
import {
  countryFilterDisplayTransform,
  defaultFilterDisplayTransform,
  lockStatusFilterEnumTransform,
  orderTrackingFilterDisplayTransform
} from "../helpers";
import {AssetManagementConfigInterface} from "../interfaces";

export const AssetManagementConfig: AssetManagementConfigInterface = {
  navItems: [
    {
      route: "asset-management/general-infos",
      label: "General Infos"
    },
    {
      route: "asset-management/assets",
      label: "Assets"
    }
  ],
  assetTypeList: {
    tableColumns: [
      {
        header: "Asset Type",
        width: "150px",
        field: "assetType"
      },
      {
        header: "Custom Asset Type",
        width: "150px",
        field: "isCustomAssetType",
        transform: (value: unknown) => {
          const bool = value as boolean;
          return bool ? "Yes" : "No";
        }
      },
      {
        header: "Technology",
        width: "150px",
        field: "technologyName"
      },
      {
        header: "Accessory",
        width: "120px",
        field: "technology",
        transform: (value: unknown) => {
          const technology = value as {isAccessory?: boolean} | undefined;
          const isAccessory = technology?.isAccessory ?? false;
          return isAccessory ? "Yes" : "No";
        },
        disableSort: true
      },
      {
        header: "Asset Type Name",
        width: "200px",
        field: "defaultAssetName"
      },
      {
        header: "Lock",
        width: "140px",
        field: "lockStatus"
      },
      {
        header: "Documentation by",
        width: "170px",
        field: "responsibleCompany"
      },
      {
        header: "Image available",
        width: "150px",
        field: "imageAvailable",
        transform: (value: unknown) => {
          const bool = value as boolean;
          return bool ? "Yes" : "No";
        }
      },
      {
        header: "# of material Numbers",
        width: "160px",
        field: "materialnoCount"
      },
      {
        header: "# of Assets",
        width: "120px",
        field: "assetCount"
      },
      {
        header: "# of Main Assets",
        width: "140px",
        field: "mainAssetCount"
      },
      {
        header: "# of Sub Assets",
        width: "130px",
        field: "subAssetCount"
      },
      {
        header: "Comment",
        width: "235px",
        field: "comment"
      },
      {
        header: "Remove",
        width: "70px",
        field: "delete",
        deleteButton: true,
        deleteButtonDisabledField: "canNotBeDeleted",
        disableSort: true
      },
      {
        header: "Details",
        width: "110px",
        field: "details",
        hasDetails: true,
        disableSort: true
      }
    ],
    headerFilters: [
      {
        filterKey: AssetTypeFilterEnum.Incomplete_Datasets,
        label: "Incomplete datasets only",
        active: false
      },
      {
        filterKey: AssetTypeFilterEnum.Lock_Status,
        label: "Locked: new only",
        active: false,
        activeValue: AssetTypeLockStatusEnum.New,
        inactiveValue: ""
      },
      {
        filterKey: AssetTypeFilterEnum.Custom_Assets,
        label: "Hide Custom Asset Types",
        active: false,
        activeValue: "false"
      },
      {
        filterKey: AssetTypeFilterEnum.Custom_Assets,
        label: "Only Custom Asset Types",
        active: false,
        activeValue: "true"
      },
      {
        filterKey: TechnologyFilterEnum.Is_Accessory,
        label: "Only Accessories",
        active: false,
        activeValue: true,
        inactiveValue: false
      }
    ],
    displayFilters: [
      {
        filterName: BaseFilterEnum.Query_Filter,
        transform: defaultFilterDisplayTransform
      },
      {
        filterName: AssetTypeFilterEnum.Technology_ID,
        transform: defaultFilterDisplayTransform
      },
      {
        filterName: AssetTypeFilterEnum.Responsible_Company,
        transform: defaultFilterDisplayTransform
      },
      {
        filterName: AssetTypeFilterEnum.Business_Unit,
        transform: defaultFilterDisplayTransform
      },
      {
        filterName: AssetTypeFilterEnum.Lock_Status,
        transform: lockStatusFilterEnumTransform
      }
    ]
  },
  assetTypeAssetsList: {
    tableColumns: [
      {
        header: "Asset ID",
        width: "130px",
        field: "assetId"
      },
      {
        header: "Material Number",
        width: "135px",
        field: "materialNumberProduction"
      },
      {
        header: "SAP Name",
        width: "240px",
        field: "equipmentNameSap"
      },
      {
        header: "Custom Asset Name",
        width: "200px",
        field: "assetName"
      },
      {
        header: "Customer",
        width: "190px",
        field: "customerOrg.name",
        sortField: "customerOrgName"
      },
      {
        header: "Sold to",
        width: "110px",
        field: "customerOrg.customerOrgId",
        sortField: "CustomerOrgIdAg"
      },
      {
        header: "Shipped to",
        width: "120px",
        field: "customerOrgWe.customerOrgId",
        sortField: "CustomerOrgIdWe"
      },
      {
        header: "IB1 Date",
        width: "130px",
        field: "equipmentDates.ib1Date",
        sortField: "Ib1Date"
      },
      {
        header: "Location",
        width: "165px",
        field: "location.city",
        sortField: "LocationCity"
      },
      {
        header: "Functional Place",
        width: "210px",
        field: "functionalLocation.location",
        sortField: "FunctionalLocation"
      },
      {
        header: "Order Tracking",
        width: "120px",
        field: "isInOrderTracking",
        transform: (value: unknown) => {
          const bool = value as boolean | undefined;
          return bool ? "Active" : "Inactive";
        }
      },
      {
        header: "Details",
        width: "110px",
        field: "details",
        hasDetails: true,
        disableSort: true
      }
    ],
    headerFilters: [],
    displayFilters: [
      {
        filterName: BaseFilterEnum.Query_Filter,
        transform: defaultFilterDisplayTransform
      },
      {
        filterName: AssetFilterEnum.Country,
        transform: countryFilterDisplayTransform
      },
      {
        filterName: AssetFilterEnum.Customer_Org_Id,
        transform: defaultFilterDisplayTransform
      },
      {
        filterName: AssetFilterEnum.Trumpf_Subsidiary,
        transform: defaultFilterDisplayTransform
      },
      {
        filterName: AssetFilterEnum.In_Order_Tracking,
        transform: defaultFilterDisplayTransform
      }
    ]
  },
  assetsList: {
    tableColumns: [
      {
        header: "Asset ID",
        width: "110px",
        field: "assetId"
      },
      {
        header: "Material Number",
        width: "115px",
        field: "materialNumberProduction"
      },
      {
        header: "Main Asset",
        width: "95px",
        field: "rootAssetId",
        sortField: "rootAssetId",
        transform: (value: unknown) => {
          const bool = value as boolean;
          return !bool ? "Yes" : "No";
        }
      },
      {
        header: "SAP Name",
        width: "220px",
        field: "equipmentNameSap"
      },
      {
        header: "Asset Name",
        width: "180px",
        field: "assetName"
      },
      {
        header: "Customer",
        width: "170px",
        field: "customerOrg.name",
        sortField: "customerOrgName"
      },
      {
        header: "Sold to",
        width: "90px",
        field: "customerOrg.customerOrgId",
        sortField: "CustomerOrgIdAg"
      },
      {
        header: "Shipped to",
        width: "100px",
        field: "customerOrgWe.customerOrgId",
        sortField: "CustomerOrgIdWe"
      },
      {
        header: "IB1 Date",
        width: "110px",
        field: "equipmentDates.ib1Date",
        sortField: "ib1Date"
      },
      {
        header: "Location",
        width: "150px",
        field: "location.city",
        sortField: "LocationCity"
      },
      {
        header: "Lock Type",
        width: "215px",
        field: "lockStatus"
      },
      {
        header: "Spare Part Catalog",
        width: "120px",
        field: "sparepartCatalog",
        sortField: "isSparepartCatalogAvailable",
        transform: (value: unknown) => {
          return value ? "Yes" : "No";
        }
      },
      {
        header: "Order Tracking",
        width: "120px",
        field: "isInOrderTracking",
        transform: (value: unknown) => {
          const bool = value as boolean | undefined;
          return bool ? "Active" : "Inactive";
        }
      },
      {
        header: "Details",
        width: "110px",
        field: "details",
        hasDetails: true,
        disableSort: true
      }
    ],
    headerFilters: [
      {
        filterKey: AssetFilterEnum.Lock_Status,
        label: "Locked Only",
        active: false,
        activeValue: LockStatusFilterEnum.Locked,
        inactiveValue: ""
      },
      {
        filterKey: AssetFilterEnum.Custom_Asset,
        label: "Hide Custom Assets",
        active: false,
        activeValue: "false"
      },
      {
        filterKey: AssetFilterEnum.Custom_Asset,
        label: "Only Custom Assets ",
        active: false,
        activeValue: "true"
      },
      {
        filterKey: AssetFilterEnum.Root_Asset,
        label: "Main Assets only",
        active: false,
        activeValue: true
      },
      {
        filterKey: AssetFilterEnum.Sparepart_Catalog_Available,
        label: "Spare Part Catalog Available",
        active: false,
        activeValue: true
      }
    ],
    displayFilters: [
      {
        filterName: BaseFilterEnum.Query_Filter,
        transform: defaultFilterDisplayTransform
      },
      {
        filterName: AssetFilterEnum.Country,
        transform: countryFilterDisplayTransform
      },
      {
        filterName: AssetFilterEnum.Customer_Org_Id,
        transform: defaultFilterDisplayTransform
      },
      {
        filterName: AssetFilterEnum.Trumpf_Subsidiary,
        transform: defaultFilterDisplayTransform
      },
      {
        filterName: AssetFilterEnum.Asset_Type,
        transform: defaultFilterDisplayTransform
      },
      {
        filterName: AssetFilterEnum.Technology_ID,
        transform: defaultFilterDisplayTransform
      },
      {
        filterName: AssetFilterEnum.In_Order_Tracking,
        transform: orderTrackingFilterDisplayTransform
      }
    ]
  },
  technologiesList: {
    tableColumns: [
      {
        header: "Technology ID",
        width: "335px",
        field: "id",
        sortField: "technologyId"
      },
      {
        header: "Default Name",
        width: "335px",
        field: "name"
      },
      {
        header: "Number of Translations",
        width: "335px",
        field: "translationsCount",
        sortField: "numberOfTranslations"
      },
      {
        header: "Number of Asset Types",
        width: "335px",
        field: "assetTypeCount"
      },
      {
        header: "Number of Assets",
        width: "260px",
        field: "assetCount"
      },
      {
        header: "Remove Technology",
        width: "170px",
        field: "delete",
        deleteButton: true,
        deleteButtonDisabledField: "canNotBeDeleted",
        disableSort: true
      },
      {
        header: "Details",
        width: "110px",
        field: "details",
        hasDetails: true,
        disableSort: true
      }
    ],
    headerFilters: [],
    displayFilters: [
      {
        filterName: BaseFilterEnum.Query_Filter,
        transform: defaultFilterDisplayTransform
      }
    ]
  }
};
